import React, { JSX, useEffect, useState } from 'react';
import Icons from 'components/atoms/Icons';
import SearchShare from '../SearchShare';
import AvailabilityFilter from './AvailabilityFilter';
import {
  AddFilterType,
  FacetValue,
  FilterPanelType,
  OnQueryInputChangeType,
  ProviderFilterOptionsType,
  ProviderFiltersType,
  RemoveFilterType,
  ReplaceLocationFiltersType,
  SetActiveFilterPanelType,
  SuperSpecialty,
  UpdateFilterType,
  UpdateQueryType,
} from 'components/types/FilterTypes';
import {
  BuildAddress,
  SetLatLng,
} from 'components/types/LocationFunctionTypes';
import SpecialtyFilter from '../../shared/filters/SpecialtyFilter';
import StandardFilter from '../../shared/filters/StandardFilter';
import FilterAccordionBtn from './FilterAccordionBtn';
import Toggle from './Toggle';
import FiltersSearchBar from 'components/pld/shared/filters/FiltersSearchBar';
import {
  getLanguagesSearchSrc,
  getOfficesSearchSrc,
  getSpecialtiesComboSearchSrc,
} from 'lib/util';

type ProviderFiltersProps = {
  clearAllFilters: Function;
  filterPanels: Array<FilterPanelType>;
  query?: string;
  setActiveFilterPanel: SetActiveFilterPanelType;
  filters: ProviderFilterOptionsType;
  queryInputValue: string;
  onQueryInputChange: OnQueryInputChangeType;
  updateQuery: UpdateQueryType;
  updateFilter: UpdateFilterType;
  removeFilter: RemoveFilterType;
  addFilter: AddFilterType;
  selectedFacets: any;
  selectedFacetsCount: number;
  selectedFilters: ProviderFiltersType;
  replaceFilter: Function;
  activeFilterPanel?: string;
  buildAddress: BuildAddress;
  geolocate: () => void;
  geolocating: boolean;
  removeLocationFilters: () => void;
  replaceLocationFilters: ReplaceLocationFiltersType;
  setLatLng: SetLatLng;
  desktopControlsOpen?: boolean;
  searchType?: string;
  count?: number;
  closeMobileControls: () => void;
  controlPanel?: string;
  resultsCount?: number;
  superSpecialties: Array<SuperSpecialty>;
};

const ProviderFilters = (props: ProviderFiltersProps): JSX.Element => {
  const {
    clearAllFilters,
    filterPanels,
    setActiveFilterPanel,
    removeFilter,
    addFilter,
    selectedFacets,
    selectedFacetsCount,
    selectedFilters,
    updateFilter,
    replaceFilter,
    activeFilterPanel,
    closeMobileControls,
    resultsCount,
    superSpecialties,
    filters,
  } = props;

  const {
    acceptingnewpatients,
    availability,
    gender,
    specscombo,
    languages,
    locationnames,
    onlinescheduling,
  } = props.filters;

  const [showAll, setShowAll] = useState<null | boolean>(null);
  const [sortedSpecialties, setSortedSpecialties] = useState<Array<FacetValue>>(
    []
  );
  const [sortedOffices, setSortedOffices] = useState<Array<FacetValue>>([]);
  const [sortedLanguages, setSortedLanguages] = useState<Array<FacetValue>>([]);
  const [searchType, setSearchType] = useState('');
  const [filterSearchResults, setFilterSearchResults] =
    useState<Array<any> | null>(null);
  const [filterSearchFacets, setFilterSearchFacets] =
    useState<Array<FacetValue> | null>(null);

  const sortSpecialties = () => {
    if (sortedSpecialties) {
      const sorted = [...sortedSpecialties].sort((a, b) => {
        const aSelected = selectedFilters.specscombo?.includes(a.text);
        const bSelected = selectedFilters.specscombo?.includes(b.text);
        return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
      });
      return sorted;
    } else {
      return [];
    }
  };

  const sortOffices = () => {
    if (locationnames) {
      const sorted = [...locationnames].sort((a, b) => {
        const aSelected = selectedFilters.locationnames?.includes(a.text);
        const bSelected = selectedFilters.locationnames?.includes(b.text);
        return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
      });
      return sorted;
    } else {
      return [];
    }
  };

  const sortLanguages = () => {
    if (languages) {
      const sorted = [...languages].sort((a, b) => {
        const aSelected = selectedFilters.languages?.includes(a.text);
        const bSelected = selectedFilters.languages?.includes(b.text);
        return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
      });
      return sorted;
    } else {
      return [];
    }
  };

  const clearResults = () => {
    setFilterSearchResults(null);
    setFilterSearchFacets(null);
  };

  useEffect(() => {
    if (sortedSpecialties?.length === 0) {
      setSortedSpecialties(specscombo);
    }
  }, [specscombo]);

  useEffect(() => {
    if (sortedOffices?.length === 0) {
      setSortedOffices(locationnames);
    }
  }, [locationnames]);

  useEffect(() => {
    if (sortedLanguages?.length === 0) {
      setSortedLanguages(languages);
    }
  }, [languages]);

  useEffect(() => {
    if (selectedFacets?.specscombo?.length > 0) {
      setSortedSpecialties(sortSpecialties());
    } else {
      setSortedSpecialties(specscombo);
    }
  }, [selectedFacets.specscombo]);

  useEffect(() => {
    if (filterSearchResults) {
      const newFacets = Array<FacetValue>();
      filterSearchResults.map(result => {
        const matchingFacet =
          searchType === 'specsandcfs'
            ? specscombo.find(filter => filter.text === result.name)
            : searchType === 'offices'
              ? locationnames.filter(
                  filter => filter.text === result.locationname
                )
              : languages.find(filter => filter.text === result.name);

        Array.isArray(matchingFacet)
          ? matchingFacet.forEach(facet => {
              if (
                !(newFacets.filter(filter => filter.id === facet.id).length > 0)
              )
                newFacets.push(facet);
            })
          : matchingFacet && newFacets.push(matchingFacet);
      });
      setFilterSearchFacets(newFacets);
    } else {
      setFilterSearchFacets(null);
    }
  }, [filterSearchResults]);

  return (
    <div className="filter-bar-wrapper">
      <h2 className="screen-recording-only only-desktop" tabIndex={0}>
        Filters
      </h2>
      <div className="filter-bar">
        <div className="filter-title-bar">
          <div
            className="close-button only-mobile"
            onClick={() => closeMobileControls()}
          >
            <Icons id="close" className="icon icon-close" aria-hidden="true" />
          </div>
          <h3 className="filters-title only-mobile">Filters</h3>
        </div>
        <div className="filters">
          <Toggle
            filter={'acceptingnewpatients'}
            checked={
              selectedFacets.acceptingnewpatients[0]
                ? selectedFacets.acceptingnewpatients[0].text == 'true'
                : false
            }
            onChange={(checked: boolean) => {
              acceptingnewpatients
                ? checked
                  ? addFilter(
                      'acceptingnewpatients',
                      true,
                      acceptingnewpatients.filter(val => val.text == 'true')[0]
                    )
                  : removeFilter(
                      'acceptingnewpatients',
                      false,
                      acceptingnewpatients.filter(val => val.text == 'true')[0]
                    )
                : removeFilter(
                    'acceptingnewpatients',
                    false,
                    selectedFacets.acceptingnewpatients.filter(
                      (val: FacetValue) => val.text == 'true'
                    )[0]
                  );
            }}
            label={'Accepting New Patients'}
            disabled={
              selectedFacets?.acceptingnewpatients &&
              selectedFacets.acceptingnewpatients[0]?.text === 'true'
                ? false
                : !filters?.acceptingnewpatients?.find(
                      obj => obj.text === 'true'
                    )
                  ? true
                  : false
            }
          ></Toggle>
          {filterPanels.map((filterPanel: FilterPanelType) => {
            if (
              filterPanel.id === 'search' ||
              filterPanel.id === 'location' ||
              filterPanel.id === 'preferredProvider'
            ) {
              return null;
            }
            const menuHeight = filterPanel.active ? { maxHeight: '100%' } : {};

            return (
              <div key={filterPanel.id}>
                <FilterAccordionBtn
                  setActiveFilterPanel={setActiveFilterPanel}
                  filterPanel={filterPanel}
                  activeFilterPanel={activeFilterPanel}
                  selectedFilters={selectedFilters}
                  clearResults={clearResults}
                  setSortedOffices={setSortedOffices}
                  sortOffices={sortOffices}
                  setSortedLanguages={setSortedLanguages}
                  sortLanguages={sortLanguages}
                  filters={filters}
                ></FilterAccordionBtn>
                <div>
                  <div className="expanded-filters" style={menuHeight}>
                    <>
                      {filterPanel.id === 'availability' &&
                        filterPanel.active && (
                          <AvailabilityFilter
                            filters={availability}
                            updateFilter={updateFilter}
                            replaceFilter={replaceFilter}
                            selectedFilters={selectedFilters}
                            setActiveFilterPanel={setActiveFilterPanel}
                          />
                        )}
                    </>
                    <>
                      {filterPanel.id === 'specscombo' &&
                        filterPanel.active && (
                          <>
                            <FiltersSearchBar
                              placeholder="Specialty or clinical areas of focus"
                              setFilterSearchResults={setFilterSearchResults}
                              entity="specsandcfs"
                              source={getSpecialtiesComboSearchSrc()}
                              setSearchType={setSearchType}
                            ></FiltersSearchBar>
                            {filterSearchFacets &&
                              filterSearchFacets.length === 0 && (
                                <p>No results found.</p>
                              )}
                            {!filterSearchFacets ||
                            filterSearchFacets?.length === 0 ? (
                              <>
                                <p className="list-label">Popular</p>
                                <SpecialtyFilter
                                  addFilter={addFilter}
                                  filters={specscombo}
                                  filterCategory="specscombo"
                                  placeholder="Enter specialty"
                                  removeFilter={removeFilter}
                                  selectedData={selectedFilters.specscombo}
                                  title="Specialties"
                                  superSpecialties={superSpecialties}
                                ></SpecialtyFilter>
                                <p
                                  className={
                                    showAll
                                      ? 'full-list-label shown'
                                      : 'full-list-label hidden'
                                  }
                                  onClick={() => setShowAll(!showAll)}
                                >
                                  {showAll
                                    ? 'Hide Full List'
                                    : 'View Full List'}
                                  {selectedFilters.specscombo.length > 0 &&
                                    ` (${selectedFilters.specscombo.length})`}
                                </p>
                              </>
                            ) : (
                              <StandardFilter
                                addFilter={addFilter}
                                filters={filterSearchFacets}
                                filterCategory="specscombo"
                                placeholder="Enter specialty"
                                removeFilter={removeFilter}
                                selectedData={selectedFilters.specscombo}
                                title="Specialties"
                              />
                            )}
                            {!filterSearchFacets && showAll && (
                              <div className="full-list">
                                <StandardFilter
                                  addFilter={addFilter}
                                  filters={sortedSpecialties}
                                  filterCategory="specscombo"
                                  placeholder="Enter specialty"
                                  removeFilter={removeFilter}
                                  selectedData={selectedFilters.specscombo}
                                  title="Specialties"
                                  noCount={true}
                                />
                              </div>
                            )}
                          </>
                        )}
                    </>
                    <>
                      {filterPanel.id === 'gender' && filterPanel.active && (
                        <StandardFilter
                          addFilter={addFilter}
                          filters={gender}
                          filterCategory="gender"
                          placeholder="Enter gender"
                          removeFilter={removeFilter}
                          selectedData={selectedFilters.gender}
                          title="Gender"
                        />
                      )}
                    </>
                    <>
                      {filterPanel.id === 'languages' && filterPanel.active && (
                        <>
                          <FiltersSearchBar
                            placeholder="Languages"
                            setFilterSearchResults={setFilterSearchResults}
                            entity="languages"
                            source={getLanguagesSearchSrc()}
                            setSearchType={setSearchType}
                          ></FiltersSearchBar>
                          {filterSearchFacets &&
                            filterSearchFacets.length === 0 && (
                              <p>No results found.</p>
                            )}
                          {!filterSearchFacets ||
                          filterSearchFacets?.length === 0 ? (
                            <StandardFilter
                              addFilter={addFilter}
                              filters={sortedLanguages}
                              filterCategory="languages"
                              placeholder="Enter language"
                              removeFilter={removeFilter}
                              selectedData={selectedFilters.languages}
                              title="Languages Spoken"
                            />
                          ) : (
                            <StandardFilter
                              addFilter={addFilter}
                              filters={filterSearchFacets}
                              filterCategory="languages"
                              placeholder="Enter language"
                              removeFilter={removeFilter}
                              selectedData={selectedFilters.languages}
                              title="Languages Spoken"
                            />
                          )}
                        </>
                      )}
                    </>
                    <>
                      {filterPanel.id === 'locationnames' &&
                        filterPanel.active && (
                          <>
                            <FiltersSearchBar
                              placeholder="Offices"
                              setFilterSearchResults={setFilterSearchResults}
                              entity="offices"
                              source={getOfficesSearchSrc()}
                              setSearchType={setSearchType}
                            ></FiltersSearchBar>
                            {filterSearchFacets &&
                              filterSearchFacets.length === 0 && (
                                <p>No results found.</p>
                              )}
                            {!filterSearchFacets ||
                            filterSearchFacets?.length === 0 ? (
                              <StandardFilter
                                addFilter={addFilter}
                                filters={sortedOffices}
                                filterCategory="locationnames"
                                placeholder="Enter office name"
                                removeFilter={removeFilter}
                                selectedData={selectedFilters.locationnames}
                                title="Offices"
                              />
                            ) : (
                              <StandardFilter
                                addFilter={addFilter}
                                filters={filterSearchFacets}
                                filterCategory="locationnames"
                                placeholder="Enter office name"
                                removeFilter={removeFilter}
                                selectedData={selectedFilters.locationnames}
                                title="Offices"
                              />
                            )}
                          </>
                        )}
                    </>
                  </div>
                </div>
              </div>
            );
          })}
          <Toggle
            filter={'onlinescheduling'}
            checked={
              selectedFacets.onlinescheduling[0]
                ? selectedFacets.onlinescheduling[0].text == 'true'
                : false
            }
            onChange={(checked: boolean) => {
              onlinescheduling
                ? checked
                  ? addFilter(
                      'onlinescheduling',
                      true,
                      onlinescheduling.filter(val => val.text == 'true')[0]
                    )
                  : removeFilter(
                      'onlinescheduling',
                      false,
                      onlinescheduling.filter(val => val.text == 'true')[0]
                    )
                : removeFilter(
                    'onlinescheduling',
                    false,
                    selectedFacets.onlinescheduling.filter(
                      (val: FacetValue) => val.text == 'true'
                    )[0]
                  );
            }}
            label={'Online Scheduling '}
            disabled={
              selectedFacets?.onlinescheduling &&
              selectedFacets.onlinescheduling[0]?.text === 'true'
                ? false
                : !filters?.onlinescheduling?.find(obj => obj.text === 'true')
                  ? true
                  : false
            }
          ></Toggle>
        </div>
        <div className="only-desktop">
          <SearchShare active={true} />
        </div>
        <div className="bg-primary btns-container">
          {selectedFacetsCount ? (
            <button
              className="btn-secondary no-icon only-mobile"
              onClick={() => {
                clearAllFilters();
              }}
              aria-label={`Clear All Filters`}
            >
              Clear All
            </button>
          ) : null}
          <button
            className="view-all-btn btn-primary no-icon btn-fw only-mobile"
            onClick={() => closeMobileControls()}
          >
            Show {resultsCount} Results
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderFilters;
